import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import TransactionOverviewForWalletOrClient from 'routes/Transactions/Details/TransactionsOverviewForWalletOrClient/TransactionOverviewForWalletOrClientTable'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links, walletDetailsRouteUrl } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

import WalletDetailsPage from './WalletDetailsPage'
import WalletDetailsTradingAccounts from './WalletDetailsTradingAccounts'

export enum WalletTab {
  Details = 'details',
  Trading_accounts = 'trading-accounts',
  Transactions = 'transactions',
}

const ResolveWalletRoute = (): JSX.Element => {
  const { id, slug } = useParams<{ id: string; slug: WalletTab }>()

  const [auth] = useContext(AuthSessionContext)
  const [crumbs, setCrumbs] = useState([{ title: 'Wallets', link: links.recordsWallets.url }])
  const walletsApiClient = useApiClient(WalletsApiClient)

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? walletsApiClient.getWalletById(id) : Promise.resolve(null)),
    [id]
  )
  const { data: wallet, isLoading } = useFetchOne(callback)
  const permissions = auth?.permissions || []

  useEffect(() => {
    if (wallet) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: wallet.name,
          link: walletDetailsRouteUrl(wallet, matchesSlug(slug) ? slug! : WalletTab.Details),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [wallet]) // eslint-disable-line react-hooks/exhaustive-deps

  const matchesSlug = (walletDetailsSlug?: WalletTab) => {
    if (!walletDetailsSlug) {
      return false
    }
    return (
      walletDetailsSlug === WalletTab.Details ||
      walletDetailsSlug === WalletTab.Trading_accounts ||
      walletDetailsSlug === WalletTab.Transactions
    )
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!wallet) {
      return <PageNotFound />
    }
    if (slug === WalletTab.Details && hasPermission(permissions, PermissionsEnum.CanViewWallet)) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WalletDetailsPage data={wallet} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      slug === WalletTab.Trading_accounts &&
      hasPermission(permissions, PermissionsEnum.CanViewWalletTradingAccounts)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <WalletDetailsTradingAccounts data={wallet} />
        </React.Fragment>
      )
    }
    if (
      slug === WalletTab.Transactions &&
      hasPermission(permissions, PermissionsEnum.CanViewWalletTransactions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TransactionOverviewForWalletOrClient wallet={wallet} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

export default ResolveWalletRoute
